const RECURRING_COPY_PAGE_SOURCES = [
  "premium",
  "promo",
  "promo/subs3",
  "plan",
  "quiz",
  "limited-offer",
  "deals",
  "promo/deals",
  "special",
];

const normalizePageSource = (pageSource) => String(pageSource || "").replace(/^\/+/, "");

export const getRecurringDurationAmount = (content = {}) => {
  const direct = Number(content.validity_period_amount);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const periodMatch = String(content.price_text_period || "")
    .match(/\/\s*(\d+)\s*(?:month|bulan)/i);
  if (periodMatch) return Number(periodMatch[1]);

  const nameMatch = String(content.name || "").match(/(\d+)\s*(?:bulan|month)/i);
  return nameMatch ? Number(nameMatch[1]) : 1;
};

export const usesLocalizedCheckoutRecurringTerms = (pageSource) => (
  RECURRING_COPY_PAGE_SOURCES.includes(normalizePageSource(pageSource))
);

const firstText = (values) => values.find((value) => (
  value !== undefined && value !== null && String(value).trim()
));

const formatRecurringAmount = (content = {}) => {
  const explicitText = firstText([
    content.recurring_price_text,
    content.next_cycle_price_text,
  ]);
  if (explicitText) {
    const extractedExplicitAmount = extractRecurringAmount(explicitText);
    if (extractedExplicitAmount) return extractedExplicitAmount;
  }

  const rawAmount = content.recurring_price !== undefined && content.recurring_price !== null
    ? Number(content.recurring_price)
    : Number(content.next_cycle_price);
  if (!Number.isFinite(rawAmount)) return null;

  const currency = String(content.currency_code || content.currency || "IDR").toUpperCase();
  if (currency === "IDR") {
    return `Rp${Math.round(rawAmount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  }
  if (currency === "USD") return `$${rawAmount.toFixed(2)}`;
  if (currency === "SGD") return `S$${rawAmount.toFixed(2)}`;
  if (currency === "JPY") return `¥${Math.round(rawAmount).toLocaleString("ja-JP")}`;
  return `${currency} ${rawAmount}`;
};

const formatStructuredRecurringAmount = (content = {}) => {
  const rawAmount = content.recurring_price !== undefined && content.recurring_price !== null
    ? Number(content.recurring_price)
    : Number(content.next_cycle_price);
  if (!Number.isFinite(rawAmount)) return null;

  const currency = String(content.currency_code || content.currency || "IDR").toUpperCase();
  if (currency === "IDR") {
    return `Rp${Math.round(rawAmount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  }
  if (currency === "USD") return `$${rawAmount.toFixed(2)}`;
  if (currency === "SGD") return `S$${rawAmount.toFixed(2)}`;
  if (currency === "JPY") return `¥${Math.round(rawAmount).toLocaleString("ja-JP")}`;
  return `${currency} ${rawAmount}`;
};

const extractRecurringAmount = (rawText) => {
  if (!rawText) return null;

  const text = String(rawText).replace(/\s+/g, " ").trim();
  // Extract a currency-bearing amount only. Never feed surrounding prose or an
  // existing cadence suffix back into the localized sentence.
  const currencyAmount = text.match(
    /(?:Rp|S\$|HK\$|A\$|C\$|US\$|[$£€¥])\s*\d[\d.,]*|(?:IDR|USD|SGD|JPY|GBP|EUR|HKD|AUD|CAD)\s*\d[\d.,]*/i,
  );
  return currencyAmount ? currencyAmount[0].trim() : null;
};

const isZeroCurrencyAmount = (rawText) => {
  const amount = extractRecurringAmount(rawText);
  if (!amount) return false;
  const digits = amount.replace(/[^0-9]/g, "");
  return !!digits && /^0+$/.test(digits);
};

export const extractCleengCurrencyAmount = extractRecurringAmount;

/**
 * Keep terms next-payment digits identical to the visible summary total.
 * BE voucher terms_text mrounds IDR to 1000 (Rp113.280 -> Rp113.000).
 * Skip free/zero summaries so trial next-cycle amounts stay intact.
 */
export function alignTermsAmountToSummaryPrice(termsText, summaryPriceText) {
  if (termsText === null || termsText === undefined || termsText === "") {
    return termsText;
  }

  const summaryAmountText = extractRecurringAmount(summaryPriceText);
  if (!summaryAmountText) {
    return termsText;
  }

  const summaryDigits = String(summaryAmountText).replace(/[^\d]/g, "");
  if (!summaryDigits || /^0+$/.test(summaryDigits)) {
    return termsText;
  }

  const termsAmountText = extractRecurringAmount(termsText);
  if (!termsAmountText || termsAmountText === summaryAmountText) {
    return termsText;
  }

  return String(termsText).replace(termsAmountText, summaryAmountText);
}

// Named-object adapter used by the 21 AUG checkout producers. Keep the older
// positional helper as the source of truth for existing callers.
export const alignCheckoutSummaryTermsText = ({ termsText, summaryPriceText }) => (
  alignTermsAmountToSummaryPrice(termsText, summaryPriceText)
);

export const buildLocalizedCheckoutRecurringTerms = ({
  content = {},
  displayTermsText,
  voucherTermsText,
  voucherFinalPriceText,
  summaryPriceText,
  voucherTypeSlug,
  language = "en",
  pageSource,
  // Trello 447 B4: a BOGO CHILD voucher's recurring cadence is always a
  // 1-month package charged monthly, no matter which parent package (1/2/3/6
  // months) funded the free cycle. Strict === true so an old/cached
  // /vouchers/apply response missing is_child_voucher degrades to today's
  // (MAIN-voucher) behaviour instead of silently mis-rendering.
  isChildVoucher = false,
}) => {
  if (!usesLocalizedCheckoutRecurringTerms(pageSource)) return null;
  if (content.type && String(content.type).toLowerCase() !== "subscription") return null;

  // Affiliate codes (Sales Admin "First Cycle" vs "Last Cycle" default discount) charge a
  // DIFFERENT amount on cycle 2+ than on the first cycle. The BE already puts the ">1"
  // amount in the voucher terms_text, so it must never be aligned to the first charge.
  const isAffiliateVoucher = String(voucherTypeSlug || "").toLowerCase() === "affiliate";

  // The landing-page producer forwards the selected plan's display contract.
  const rawTermsText = firstText([
    voucherTermsText,
    displayTermsText,
    content.terms_text,
    content.pricing && content.pricing.next_billing_text,
  ]);
  // Discount vouchers already put the first-payment amount in terms_text.
  // That amount must beat package retail recurring_price (mfspromo: Rp80.000
  // vs Rp125.000). Structured price still beats stale per-month prose when
  // there is no voucher currency amount.
  const voucherRecurringAmount = isAffiliateVoucher
    ? extractRecurringAmount(voucherTermsText)
    : (extractRecurringAmount(voucherTermsText) || extractRecurringAmount(voucherFinalPriceText));
  let recurringAmount = voucherRecurringAmount
    || formatStructuredRecurringAmount(content)
    || extractRecurringAmount(rawTermsText)
    || formatRecurringAmount(content);

  // The WRAPPING package's own duration (1/2/3/6 months). This describes which
  // package cycle 1 actually belongs to and is correct for MAIN and CHILD
  // alike (a child's cycle-1 legitimately runs the parent's full duration) —
  // it gates the amount-priority branch just below. It is deliberately kept
  // separate from `duration` (the RENDERED cadence, further down), which for
  // a child collapses to 1 regardless of packageDuration. Trello 447 B1/B4.
  const packageDuration = getRecurringDurationAmount(content);

  // /special affiliate-external plans can have a free first cycle (`Rp0`) while
  // Sales Admin configures a positive renewal price. The voucher's final price
  // is the current charge, not the recurring amount; keep it from overriding the
  // selected plan's forwarded next-billing contract on both checkout surfaces.
  const isSpecialFreeFirstCycle = normalizePageSource(pageSource) === "special"
    && (
      isZeroCurrencyAmount(voucherFinalPriceText)
      || isZeroCurrencyAmount(summaryPriceText)
    );
  if (isSpecialFreeFirstCycle) {
    // Trello 447 B1: BE commit 0574359eb deliberately made the get-plan
    // CARD's next_billing_text (forwarded here as displayTermsText) PER-MONTH
    // for MAIN vouchers, so it can only stay first in the priority list for a
    // 1-month package (per-month === per-cycle there, and every existing
    // T398/T399 first-paint test depends on this exact order). Once the
    // wrapping package spans more than one month, displayTermsText must move
    // LAST so it can never clobber the correct PER-CYCLE total that the
    // backend's own checkout-terms strings (voucherTermsText/content.terms_text,
    // from setTermsTextForSubscription()) already carry for both MAIN and CHILD.
    const configuredRecurringAmount = (packageDuration > 1
      ? [
        extractRecurringAmount(voucherTermsText),
        extractRecurringAmount(content.terms_text),
        extractRecurringAmount(content.pricing && content.pricing.next_billing_text),
        formatStructuredRecurringAmount(content),
        formatRecurringAmount(content),
        extractRecurringAmount(displayTermsText),
      ]
      : [
        extractRecurringAmount(displayTermsText),
        extractRecurringAmount(content.terms_text),
        extractRecurringAmount(content.pricing && content.pricing.next_billing_text),
        formatStructuredRecurringAmount(content),
        formatRecurringAmount(content),
      ]).find((amount) => amount && !isZeroCurrencyAmount(amount));
    // Prefer the already-selected Sales Admin contract from the first render;
    // do not wait for the async voucher response to replace a package fallback.
    recurringAmount = configuredRecurringAmount || recurringAmount;
  }
  if (!recurringAmount) return null;

  // Trello 447 B4: render the RENDERED cadence, not the wrapping package's own
  // duration, for a BOGO child — its recurring charge is always the 1-month
  // package, every month, regardless of whether the parent ran 1/2/3/6 months.
  const duration = isChildVoucher === true ? 1 : packageDuration;
  const nextTermsText = language === "id"
    ? `Berikutnya kamu akan ditagih ${recurringAmount} setiap ${duration === 1 ? "bulan" : `${duration} bulan`} sampai dibatalkan.`
    : `Next you will be charged ${recurringAmount} every ${duration === 1 ? "month" : `${duration} months`} until canceled.`;

  if (isAffiliateVoucher) return nextTermsText;

  return alignTermsAmountToSummaryPrice(
    nextTermsText,
    summaryPriceText || voucherFinalPriceText,
  );
};
